import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Loader2, Truck, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cadastrarEmpresa } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import ContratoEntregador from '@/components/ContratoEntregador';

const Cadastro = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { config } = useRegion();
  
  const telefone = location.state?.telefone || '';
  const [loading, setLoading] = useState(false);
  const [showContrato, setShowContrato] = useState(false);
  const [empresaCriada, setEmpresaCriada] = useState(null);
  const [usuarioCriado, setUsuarioCriado] = useState(null);
  const [formData, setFormData] = useState({
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    email: '',
    tipo_empresa: '',
    segmento: ''
  });
  
  // Segmentos disponíveis
  const segmentos = [
    { value: 'restaurante', label: '🍕 Restaurante / Pizzaria / Fast-food' },
    { value: 'farmacia', label: '💊 Farmácia / Drogaria' },
    { value: 'supermercado', label: '🛒 Supermercado / Mercearia' },
    { value: 'florista', label: '🌸 Florista / Floricultura' },
    { value: 'pet_shop', label: '🐕 Pet Shop / Veterinária' },
    { value: 'loja', label: '📦 Loja / E-commerce' },
    { value: 'confeitaria', label: '🍰 Confeitaria / Doceria' },
    { value: 'padaria', label: '🥖 Padaria / Lanchonete' },
    { value: 'marmita', label: '🥗 Marmitas / Refeições' },
    { value: 'escritorio', label: '📄 Escritório / Documentos' },
    { value: 'autopecas', label: '🔧 Autopeças / Oficina' },
    { value: 'presentes', label: '🎁 Presentes / Cestas' },
    { value: 'outro', label: '📋 Outro segmento' }
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Sanitiza dados antes de enviar: remove campos vazios opcionais
      const payload = { ...formData };
      // Segmento só faz sentido para clientes; entregadores e ambos podem ficar sem
      if (!payload.segmento || payload.tipo_empresa === 'entregador') {
        delete payload.segmento;
      }
      // Nome fantasia vazio vira null (não envia)
      if (!payload.nome_fantasia || !payload.nome_fantasia.trim()) {
        delete payload.nome_fantasia;
      }
      
      const response = await cadastrarEmpresa(telefone, payload);
      
      if (response.data.success) {
        const perfil = response.data.usuario.perfil;
        
        // Para entregador: exige assinatura do contrato antes de logar
        if (perfil === 'entregador') {
          setEmpresaCriada(response.data.empresa);
          setUsuarioCriado(response.data.usuario);
          setShowContrato(true);
          toast.success('Cadastro registrado! Agora assine o contrato para finalizar.');
          return;
        }
        
        login(response.data.usuario, response.data.empresa);
        toast.success('Cadastro realizado com sucesso!');
        toast.info('Ative sua assinatura para começar a usar.');
        
        if (perfil === 'cliente') {
          navigate('/dashboard/cliente');
        } else if (perfil === 'entregador') {
          navigate('/dashboard/entregador');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo-rota017.png" alt="Rota 017" className="w-12 h-12 rounded-xl object-cover" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Completar Cadastro
            </h1>
            <p className="text-sm text-slate-600">WhatsApp: {telefone}</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{config.document.label} *</label>
              <Input
                data-testid="cnpj-input"
                value={formData.cnpj}
                onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                placeholder={config.document.placeholder}
                required
                className="focus:border-orange-500 focus:ring-orange-200"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de Empresa *</label>
              <select
                data-testid="tipo-empresa-select"
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none"
                value={formData.tipo_empresa}
                onChange={(e) => setFormData({...formData, tipo_empresa: e.target.value})}
                required
              >
                <option value="">Selecione o tipo de empresa</option>
                <option value="cliente">Cliente (Solicita Entregas)</option>
                <option value="entregador">{config.code === 'PT' ? 'Estafeta (Realiza Entregas)' : 'Entregador (Realiza Entregas)'}</option>
              </select>
            </div>
          </div>
          
          {/* Campo de Segmento (apenas para clientes) */}
          {formData.tipo_empresa === 'cliente' && (
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
              <label className="block text-sm font-medium text-orange-900 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Segmento do seu Negócio *
              </label>
              <select
                data-testid="segmento-select"
                className="w-full h-10 px-3 rounded-md border border-orange-200 bg-white text-slate-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none"
                value={formData.segmento}
                onChange={(e) => setFormData({...formData, segmento: e.target.value})}
                required
              >
                <option value="">Selecione o segmento</option>
                {segmentos.map((seg) => (
                  <option key={seg.value} value={seg.value}>{seg.label}</option>
                ))}
              </select>
              <p className="text-xs text-orange-600 mt-2">
                Isso nos ajuda a personalizar sua experiência na plataforma
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Razão Social *</label>
            <Input
              data-testid="razao-social-input"
              value={formData.razao_social}
              onChange={(e) => setFormData({...formData, razao_social: e.target.value})}
              required
              className="focus:border-orange-500 focus:ring-orange-200"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nome Fantasia</label>
            <Input
              value={formData.nome_fantasia}
              onChange={(e) => setFormData({...formData, nome_fantasia: e.target.value})}
              className="focus:border-orange-500 focus:ring-orange-200"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">E-mail *</label>
            <Input
              data-testid="email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="focus:border-orange-500 focus:ring-orange-200"
            />
          </div>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800">
              <strong>Valores de Assinatura:</strong><br />
              Cliente: {config.currency.symbol} {config.code === 'PT' ? '29,90' : '149,90'}/mês + {config.currency.symbol} {config.code === 'PT' ? '0,30' : '1,50'} por entrega<br />
              {config.code === 'PT' ? 'Estafeta' : 'Entregador'}: {config.currency.symbol} {config.code === 'PT' ? '12,90' : '49,90'}/semana
            </p>
          </div>
          
          <div className="text-sm text-slate-500">
            Ao cadastrar, você concorda com os <Link to="/privacidade" className="text-orange-600 hover:underline">Termos de Uso e Política de Privacidade</Link>.
          </div>
          
          <Button
            data-testid="register-button"
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cadastrando...</> : 'Concluir Cadastro'}
          </Button>
        </form>
      </div>
      
      {/* Modal de Contrato (entregador) */}
      <ContratoEntregador
        isOpen={showContrato}
        empresaId={empresaCriada?.id}
        onClose={() => {
          // Se cancelar antes de assinar, mantém na tela de cadastro
          setShowContrato(false);
          toast.warning('É necessário assinar o contrato para concluir o cadastro de entregador.');
        }}
        onAssinado={() => {
          login(usuarioCriado, { ...empresaCriada, contrato_assinado: true });
          toast.success('Bem-vindo à Rota 017!');
          navigate('/dashboard/entregador');
        }}
      />
    </div>
  );
};

export default Cadastro;
